import { Ticker } from "pixi.js";
import { Box3, Mesh, Object3D, Vector3 } from "three";
import { ICollidable } from "../../../../../../three/physics/ICollidable";
import { Physics } from "../../../../../../three/physics/Physics";
import { IProjectileContainer } from "../../gamefield/IProjectileContainer";
import { InterfaceCheckUtils } from "../../../../../../common/utils/InterfaceCheckUtils";

export abstract class Projectile extends Object3D implements ICollidable {
    protected readonly velocity: Vector3;
    protected readonly movement: Vector3;
    private readonly _boundingBox: Box3;
    
    public get boundingBox(): Box3 {
        return this._boundingBox.copy(this.weaponMesh.geometry.boundingBox).applyMatrix4(this.matrixWorld);
    }

    public abstract get collidesWithLayer(): number;

    protected thrown: boolean;

    public constructor(
        protected readonly projectileContainer: IProjectileContainer,
        protected readonly weaponMesh: Mesh,
        protected readonly speed: number,
        protected readonly damage: number = 100,
    ) {
        super();
        this.velocity = new Vector3();
        this.movement = new Vector3();
        this.thrown = false;
        this.add(weaponMesh);
        this._boundingBox = new Box3().setFromObject(weaponMesh);
        Physics.add(this);
    }

    public destroy(): void {
        Physics.remove(this);
        Ticker.shared.remove(this.onTick, this);

        this.traverse((child) => {
            if ((child as Mesh).geometry) {
                (child as Mesh).geometry.dispose();
            }

            if ((child as Mesh).material) {
                const material = (child as Mesh).material;
                if (Array.isArray(material)) {
                    material.forEach((mat) => mat.dispose());
                } else {
                    material.dispose();
                }
            }
        });

        if (this.parent) {
            this.parent.remove(this);
        }
    }

    public throwToPoint(target: Vector3): void {
        const direction = target.clone().sub(this.getWorldPosition(new Vector3())).normalize();
        this.throwAtDirection(direction);
    }

    public throwAtDirection(direction: Vector3): void {
        if (this.thrown) {
            throw new Error("You can not throw same projectile twice.");
        }
        this.thrown = true;
        const projectileLocalPosition = this.projectileContainer.worldToLocal(this.getWorldPosition(new Vector3()));
        this.parent.remove(this);
        this.projectileContainer.add(this);
        this.position.copy(projectileLocalPosition);
        Ticker.shared.add(this.onTick, this);
        this.velocity.copy(direction.normalize()).multiplyScalar(this.speed);
    }

    public move(dt: number): void {
        this.movement.copy(this.velocity).multiplyScalar(dt);
        this.position.add(this.movement);
    }

    protected updateBounds(): void { }

    protected onTick(dt: number): void {
        this.move(dt);
        this.updateBounds();
    }

    public onCollision(other: ICollidable): void {
        if (this.parent == null) {
            // We still not attached to scene. So wait...
            return;
        }
        if (other instanceof Projectile) {
            return;
        }
        if (InterfaceCheckUtils.isDamageable(other)) {
            other.takeDamage(this.damage);
        }
        this.projectileContainer.remove(this);
        this.destroy();
    }

}